/* Liquid's inventory total can include locations that cannot ship online orders. */
(() => {
  const QUERY = `query ShippingAvailability($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant { id availableForSale currentlyNotInStock }
    }
  }`;

  class ShippingAvailabilityClient {
    constructor(endpoint, { fetch: request = globalThis.fetch, ttl = 15000, timeout = 8000 } = {}) {
      Object.assign(this, { endpoint, request: request.bind(globalThis), ttl, timeout });
      this.cache = new Map();
      this.pending = new Map();
      this.queue = new Map();
    }

    get(id, { force = false } = {}) {
      const key = String(id).replace('gid://shopify/ProductVariant/', '');
      if (!/^\d+$/.test(key)) return Promise.reject(new Error('Invalid variant'));
      const cached = this.cache.get(key);
      if (!force && cached && cached.expires > Date.now()) return Promise.resolve(cached.value);
      if (this.pending.has(key)) return this.pending.get(key);
      const promise = new Promise((resolve, reject) => this.queue.set(key, { resolve, reject }));
      this.pending.set(key, promise);
      if (!this.scheduled) {
        this.scheduled = true;
        queueMicrotask(() => this.flush());
      }
      return promise;
    }

    flush() {
      this.scheduled = false;
      const entries = Array.from(this.queue);
      this.queue.clear();
      for (let offset = 0; offset < entries.length; offset += 50) this.fetchBatch(entries.slice(offset, offset + 50));
    }

    async fetchBatch(entries) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeout);
      try {
        const response = await this.request(this.endpoint, {
          method: 'POST', credentials: 'omit',
          headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
          body: JSON.stringify({ query: QUERY, variables: { ids: entries.map(([id]) => `gid://shopify/ProductVariant/${id}`) } })
        });
        if (!response.ok) throw new Error('Availability request failed');
        const result = await response.json();
        if (result.errors?.length || !Array.isArray(result.data?.nodes) || result.data.nodes.length !== entries.length) {
          throw new Error('Invalid availability response');
        }
        const values = result.data.nodes.map((node, index) => {
          if (node === null) return { available: false, backorder: false };
          if (node.id !== `gid://shopify/ProductVariant/${entries[index][0]}` ||
              typeof node.availableForSale !== 'boolean' || typeof node.currentlyNotInStock !== 'boolean') {
            throw new Error('Invalid availability response');
          }
          return { available: node.availableForSale, backorder: node.currentlyNotInStock };
        });
        entries.forEach(([id, subscriber], index) => {
          this.pending.delete(id);
          this.cache.set(id, { value: values[index], expires: Date.now() + this.ttl });
          subscriber.resolve(values[index]);
        });
      }
      catch (error) {
        console.warn('[shipping-availability]', error.message);
        entries.forEach(([id, subscriber]) => {
          this.pending.delete(id);
          this.cache.delete(id);
          subscriber.reject(error);
        });
      }
      finally { clearTimeout(timer); }
    }
  }

  class ShippingAvailabilityController {
    constructor(client, strings, root = document) {
      Object.assign(this, { client, strings, root });
      this.forms = new WeakMap();
      this.statuses = new Map();
      this.statusRequests = new Set();
    }

    variantId(form) { return form.querySelector('[name="id"]')?.value; }
    buttons(form) { return Array.from(form.elements).filter((element) => element.type === 'submit'); }
    selection(form) {
      return [this.variantId(form), ...Array.from(form.elements).filter((input) => input.name === 'bundles' && input.checked).map((input) => input.value)];
    }

    refresh(form, { nativeDisabled, resetLabels = false } = {}) {
      if (!form?.matches('form[is="product-form"]')) return;
      const id = this.variantId(form);
      if (!id) return;
      let record = this.forms.get(form);
      if (!record) {
        record = { buttons: new Map(), quantities: new Map(), nativeDisabled: this.buttons(form).every((button) => button.disabled) };
        this.forms.set(form, record);
      }
      for (const input of Array.from(form.elements).filter((element) => element.name === 'quantity')) {
        if (!record.quantities.has(input)) record.quantities.set(input, input.disabled);
      }
      if (nativeDisabled !== undefined) record.nativeDisabled = nativeDisabled;
      for (const button of this.buttons(form)) {
        if (!record.buttons.has(button) || resetLabels) {
          const label = button.querySelector('.btn-text > span:not([data-fill])') || button.querySelector('.btn-text:not(:has(svg))');
          const temporary = [this.strings.checking, this.strings.unavailable, this.strings.retry].includes(label?.textContent);
          if (!temporary || !record.buttons.has(button)) {
            record.buttons.set(button, { label, text: label?.textContent, aria: button.getAttribute('aria-label') });
          }
        }
      }
      if (record.id !== id) {
        record.id = id;
        record.state = 'pending';
        this.client.get(id).then((value) => this.resolve(form, id, value)).catch(() => this.resolve(form, id, null));
      }
      this.renderForm(form, record);
    }

    resolve(form, id, value) {
      this.statuses.set(id, value);
      for (const target of this.root.querySelectorAll('form[is="product-form"]')) {
        const record = this.forms.get(target);
        if (record?.id === id) {
          record.state = value === null ? 'error' : value.available ? 'available' : 'unavailable';
          this.renderForm(target, record);
        }
      }
      this.renderStatuses();
    }

    renderForm(form, record) {
      if (form.dataset.shippingAvailability !== record.state) form.dataset.shippingAvailability = record.state;
      const blocked = record.nativeDisabled || record.state !== 'available';
      const message = record.state === 'error' ? this.strings.retry : record.state === 'unavailable' ? this.strings.unavailable : this.strings.checking;
      for (const [button, original] of record.buttons) {
        if (!button.isConnected) { record.buttons.delete(button); continue; }
        // A failed check offers retry; the submit guard never lets this action buy.
        const disabled = record.nativeDisabled || (blocked && record.state !== 'error');
        if (button.disabled !== disabled) button.disabled = disabled;
        const text = record.state === 'available' || record.nativeDisabled ? original.text : message;
        if (original.label && original.label.textContent !== text) original.label.textContent = text;
        const aria = record.state === 'available' || record.nativeDisabled ? original.aria : message;
        if (aria !== null && button.getAttribute('aria-label') !== aria) button.setAttribute('aria-label', aria);
      }
      for (const [input, originallyDisabled] of record.quantities) {
        if (!input.isConnected) { record.quantities.delete(input); continue; }
        const disabled = originallyDisabled || blocked;
        if (input.disabled !== disabled) {
          input.disabled = disabled;
          const quantity = input.closest('quantity-input');
          if (disabled) quantity?.querySelectorAll('button').forEach((button) => { button.disabled = true; });
          else quantity?.validateQtyRules?.();
        }
      }
    }

    renderStatuses() {
      for (const status of this.root.querySelectorAll('[data-shipping-status]')) {
        const id = status.dataset.variantId;
        if (!this.statuses.has(id)) continue;
        const value = this.statuses.get(id);
        const state = value === null ? 'error' : value.available ? 'available' : 'unavailable';
        const message = value === null ? this.strings.error : !value.available ? this.strings.unavailable : value.backorder ? this.strings.backorder : this.strings.inStock;
        const text = status.querySelector('[data-shipping-message]');
        if (text && text.textContent !== message) text.textContent = message;
        const alert = status.querySelector('[data-shipping-alert]');
        const alertClass = `alert alert--${state === 'available' && !value?.backorder ? 'success' : state === 'unavailable' ? 'error' : 'info'} inline-flex items-center gap-3`;
        if (alert && alert.className !== alertClass) alert.className = alertClass;
        const retry = status.querySelector('[data-shipping-retry]');
        if (retry && retry.hidden !== (state !== 'error')) retry.hidden = state !== 'error';
      }
    }

    async retry(form) {
      const record = this.forms.get(form);
      if (!record) return;
      const id = record.id;
      record.state = 'pending';
      this.renderForm(form, record);
      try { this.resolve(form, id, await this.client.get(id, { force: true })); }
      catch { this.resolve(form, id, null); }
    }

    async requireAvailable(ids) {
      let values;
      try { values = await Promise.all([...new Set(ids.map(String))].map((id) => this.client.get(id, { force: true }))); }
      catch { throw new Error(this.strings.error); }
      if (values.some((value) => !value.available)) throw new Error(this.strings.unavailable);
    }

    scan() {
      for (const form of this.root.querySelectorAll('form[is="product-form"]')) this.refresh(form);
      for (const status of this.root.querySelectorAll('[data-shipping-status]')) {
        const id = status.dataset.variantId;
        if (this.statusRequests.has(id)) continue;
        this.statusRequests.add(id);
        const finish = (value) => { this.statuses.set(id, value); this.renderStatuses(); };
        this.client.get(id).then(finish).catch(() => finish(null));
      }
      this.renderStatuses();
    }

    async onSubmit(event) {
      const form = event.target;
      if (!form.matches('form[is="product-form"]')) return;
      this.refresh(form);
      const record = this.forms.get(form);
      if (!record) return;
      const selection = this.selection(form);
      const selectionKey = selection.join(',');
      if (record.permit === selectionKey) { record.permit = null; return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (record.state === 'error') { this.retry(form); return; }
      if (record.state !== 'available' || record.nativeDisabled || record.submitting) return;
      record.submitting = true;
      const id = record.id;
      const submitter = event.submitter;
      try {
        await this.requireAvailable(selection);
        if (!form.isConnected || this.selection(form).join(',') !== selectionKey) return;
        record.permit = selectionKey;
        form.requestSubmit(submitter?.isConnected ? submitter : undefined);
        record.permit = null;
      }
      catch (error) {
        this.resolve(form, id, this.client.cache.get(id)?.value || null);
        form.handleErrorMessage?.(error.message);
      }
      finally { record.submitting = false; }
    }

    start() {
      this.root.addEventListener('submit', (event) => this.onSubmit(event), true);
      this.root.addEventListener('change', (event) => {
        if (event.target.name === 'id') queueMicrotask(() => this.refresh(event.target.form));
      });
      this.root.addEventListener('click', (event) => {
        const retry = event.target.closest('[data-shipping-retry]');
        if (!retry) return;
        const id = retry.closest('[data-shipping-status]').dataset.variantId;
        for (const form of this.root.querySelectorAll('form[is="product-form"]')) {
          if (this.variantId(form) === id) this.retry(form);
        }
      });
      const observer = new MutationObserver(() => {
        if (this.scanQueued) return;
        this.scanQueued = true;
        queueMicrotask(() => { this.scanQueued = false; this.scan(); });
      });
      observer.observe(this.root.documentElement, { childList: true, subtree: true });
      this.scan();
    }
  }

  if (typeof module !== 'undefined') module.exports = { ShippingAvailabilityClient, ShippingAvailabilityController, QUERY };
  if (typeof document === 'undefined' || !window.theme?.shippingAvailabilityConfig) return;
  const config = window.theme.shippingAvailabilityConfig;
  theme.shippingAvailability = new ShippingAvailabilityController(new ShippingAvailabilityClient(config.endpoint), config.strings);
  theme.shippingAvailability.start();
})();
